import React, { useState, useMemo } from 'react'
import {
  Typography,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  IconButton,
  Divider,
  Box,
  Paper
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import moment from 'moment'
import clsx from 'clsx'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PeopleIcon from '@material-ui/icons/People'
import WorkIcon from '@material-ui/icons/Work'
import PersonIcon from '@material-ui/icons/Person'
import ScheduleIcon from '@material-ui/icons/Schedule'
import TodayIcon from '@material-ui/icons/Today'
import NotificationsIcon from '@material-ui/icons/Notifications'
import AssessmentIcon from '@material-ui/icons/Assessment'
import VisibilityIcon from '@material-ui/icons/Visibility'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RateReviewIcon from '@material-ui/icons/RateReview'
import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import ReplyIcon from '@material-ui/icons/Reply'
import InboxIcon from '@material-ui/icons/Inbox'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import AnnouncementIcon from '@material-ui/icons/Announcement'
import WarningIcon from '@material-ui/icons/Warning'
import StarIcon from '@material-ui/icons/Star'
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer'
import { useAuth, useFetch } from '../context/useFetch'
import i18n from '../i18n/i18n'
import { Event } from '../header'
import { GRADES_VALUES, GRADES_COLOR } from '../../src/app_config'

// Color palette for KPI types
const KPI_COLORS = {
  non_seen_applications: { main: '#007FFF', light: '#D6EAF8' },
  company_candidates: { main: '#007FFF', light: '#D6EAF8' },
  active_offers: { main: '#007FFF', light: '#D6EAF8' },
  active_users: { main: '#007FFF', light: '#D6EAF8' },
  non_qualified_candidates: { main: '#007FFF', light: '#D6EAF8' },
  candidates_of_the_day: { main: '#007FFF', light: '#D6EAF8' },
  visits: { main: '#007FFF', light: '#D6EAF8' },
  examination_percentage: { main: '#007FFF', light: '#D6EAF8' },
  qualification_percentage: { main: '#007FFF', light: '#D6EAF8' },
  reactivity_percentage: { main: '#007FFF', light: '#D6EAF8' },
  rejected_candidates_response_percentage: { main: '#007FFF', light: '#D6EAF8' },
  default: { main: '#007FFF', light: '#D6EAF8' }
}

// Icon mapping for each KPI type
const KPI_ICONS = {
  non_seen_applications: InboxIcon,
  company_candidates: InsertDriveFileIcon,
  active_offers: AnnouncementIcon,
  active_users: PeopleIcon,
  non_qualified_candidates: WarningIcon,
  candidates_of_the_day: TodayIcon,
  visits: VisibilityIcon,
  examination_percentage: CheckCircleIcon,
  qualification_percentage: StarIcon,
  reactivity_percentage: QuestionAnswerIcon,
  rejected_candidates_response_percentage: QuestionAnswerIcon,
  default: AssessmentIcon
}

const getKpiColor = (stat) => KPI_COLORS[stat] || KPI_COLORS.default
const getKpiIcon = (stat) => KPI_ICONS[stat] || KPI_ICONS.default

// Styles
const useKpisStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: theme.spacing(3, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3, 3)
    }
  },
  header: {
    marginBottom: theme.spacing(3)
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em'
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  }
}))

const useCardStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(2)
  },
  card: {
    position: 'relative',
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: 'none',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    minHeight: 180,
    '&:hover': {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.5,
      zIndex: 0
    }
  },
  cardClickable: {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)'
    }
  },
  cardContent: {
    position: 'relative',
    zIndex: 1
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  iconWrapper: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
  },
  icon: {
    fontSize: 22
  },
  value: {
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em'
  },
  label: {
    fontSize: 15,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: theme.spacing(1.5),
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  clickHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid rgba(0, 0, 0, 0.06)'
  },
  clickHintText: {
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  clickHintIcon: {
    fontSize: 16,
    color: theme.palette.text.disabled
  },
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3)
  },
  dialogIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${KPI_COLORS.candidates_of_the_day.main} 0%, ${KPI_COLORS.candidates_of_the_day.main}dd 100%)`,
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)'
  },
  dialogIconInner: {
    fontSize: 20
  },
  dialogTitleText: {
    fontWeight: 600,
    flex: 1
  },
  dialogContent: {
    padding: '0 24px 24px'
  },
  eventItem: {
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #F9FAFB 0%, #ffffff 100%)',
    borderRadius: 12,
    marginBottom: theme.spacing(1.5),
    border: '1px solid rgba(0, 0, 0, 0.04)',
    '&:last-child': {
      marginBottom: 0
    }
  }
}))

const useExpandStyles = makeStyles((theme) => ({
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    })
  },
  expandOpen: {
    transform: 'rotate(180deg)'
  }
}))

const usePercentageStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2.5)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5)
  },
  title: {
    fontSize: '1.375rem',
    fontWeight: 600
  },
  divider: {
    margin: '24px 0 16px'
  },
  progressContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #F9FAFB 0%, #ffffff 100%)',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.04)'
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  progressLabelText: {
    fontSize: '1rem'
  },
  progressLabelValue: {
    fontWeight: 700,
    fontSize: '1rem'
  },
  progressBar: {
    height: 6,
    borderRadius: 3
  },
  kpiItem: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #F9FAFB 0%, #ffffff 100%)',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.04)',
    '&:last-child': {
      marginBottom: 0
    }
  },
  kpiTitle: {
    fontSize: '1.05rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  }
}))

export const Kpis = () => {
  const classes = useKpisStyles()
  const auth = useAuth()
  const profile = useSelector((state) => state.profile)

  if (!profile || !auth.isCompany()) return null

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h2" className={classes.title}>
          {i18n.t('company_profiles.kpis.title')}
        </Typography>
      </Box>
      <KpiCard />
    </Box>
  )
}

const KpiCard = () => useFetch(
  ({ fetch }) => ({
    stats: fetch('profile/stats'),
    events: fetch('profile/user_activities')
  }),
  ({ stats: allStats, events }) => {
    const classes = useCardStyles()
    const [openDialog, setOpenDialog] = useState(false)

    const handleClick = (stat, value) => {
      if (stat === 'company_candidates') window.open('/entreprise/mycandidates', '_blank')
      if (stat === 'active_offers') window.open('/entreprise/profile/offres', '_blank')
      if (stat === 'active_users') window.open('/entreprise/profile/users', '_blank')
      if (stat === 'non_qualified_candidates') {
        localStorage.setObject('candidate-filters-2', { all: ['Status_pending'] })
        window.open('/entreprise/mycandidates', '_blank')
      }
      if (stat === 'candidates_of_the_day' && value > 0) setOpenDialog(true)
      if (stat === 'non_seen_applications') {
        localStorage.setObject('candidate-filters-2', { all: ['Status_nonSeen'] })
        window.open('/entreprise/mycandidates', '_blank')
      }
    }

    const { company_id: _companyId, ...stats } = allStats
    const clickableStats = ['company_candidates', 'active_offers', 'active_users', 'non_qualified_candidates', 'non_seen_applications']
    const filteredStats = Object.fromEntries(
      Object.entries(stats).filter(([key]) => key !== 'report')
    )
    return (
      <>
        <div className={classes.grid}>
          {Object.entries(filteredStats).map(([stat, value]) => {
            const isClickable = clickableStats.includes(stat) || (stat === 'candidates_of_the_day' && value > 0)
            const colors = getKpiColor(stat)
            const Icon = getKpiIcon(stat)

            // Memoize styles to avoid recreating on every render
            const cardStyle = useMemo(() => ({
              background: `linear-gradient(135deg, ${colors.light} 0%, #ffffff 100%)`
            }), [colors.light])

            const iconWrapperStyle = useMemo(() => ({
              background: `linear-gradient(135deg, ${colors.main} 0%, ${colors.main}dd 100%)`,
              color: '#ffffff'
            }), [colors.main])

            return (
              <Paper
                key={stat}
                elevation={0}
                onClick={() => isClickable && handleClick(stat, value)}
                className={clsx(classes.card, isClickable && classes.cardClickable)}
                style={cardStyle}
              >
                <div className={classes.cardContent}>
                  <div className={classes.cardHeader}>
                    <div
                      className={classes.iconWrapper}
                      style={iconWrapperStyle}
                    >
                      <Icon className={classes.icon} />
                    </div>
                    <Typography className={classes.value}>
                      {value || 0}
                      {stat.includes('percentage') && '%'}
                    </Typography>
                  </div>

                  <Typography className={classes.label}>
                    {i18n.t(`company_profiles.kpis.${stat}`)}
                  </Typography>

                  {isClickable && (
                    <div className={classes.clickHint}>
                      <ArrowForwardIcon className={classes.clickHintIcon} />
                    </div>
                  )}
                </div>
              </Paper>
            )
          })}
        </div>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            className: classes.dialogPaper
          }}
        >
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <div className={classes.dialogIcon}>
              <TodayIcon className={classes.dialogIconInner} />
            </div>
            <Typography variant="h6" className={classes.dialogTitleText}>
              {i18n.t('company_profiles.kpis.candidates_of_the_day')}
            </Typography>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {events
              .filter((event) => moment(event.created_at).format('DD-MM-YYYY') === moment(new Date()).format('DD-MM-YYYY'))
              .map((event) => (
                <div key={event.id || event.created_at} className={classes.eventItem}>
                  <Event event={event} />
                </div>
              ))}
          </DialogContent>
        </Dialog>
      </>
    )
  }
)

const useGradeStyles = () => (
  Object.assign(...Object.entries(GRADES_COLOR).map(([key, value]) => ({ [key]: makeStyles(value)() })))
)

/**
 * Determines the appropriate grade style based on a percentage value.
 * Grades are defined in GRADES_VALUES: grade1 (0-30), grade2 (30-50), grade3 (50-70), grade4 (70-100)
 * 
 * @param {number} value - The percentage value to grade (expected: 0-100)
 * @param {Object} classes - Material-UI style classes from useGradeStyles
 * @returns {Object} The matching grade style object, or empty object if no match/invalid value
 */
const gradeStyle = (value, classes) => {
  // Validate input - handle edge cases explicitly
  if (typeof value !== 'number' || isNaN(value)) {
    return {}
  }

  // Clamp value to valid range (0-100) to handle out-of-bounds values
  const clampedValue = Math.max(0, Math.min(100, value))

  // Find matching grade - using >= and < for clearer boundary logic
  const grade = Object.entries(GRADES_VALUES).find(([, [min, max]]) =>
    clampedValue >= min && clampedValue < max
  )?.[0]

  // Handle the edge case where value is exactly 100
  if (clampedValue === 100 && !grade) {
    return classes.grade4 || {}
  }

  if (!grade) {
    // This shouldn't happen after clamping, but defensive programming
    return {}
  }

  // Ensure the grade exists in classes
  return classes[grade] || {}
}

export const PercentageKpis = () => {
  const classes = usePercentageStyles()
  const expandClasses = useExpandStyles()
  const company = useSelector((state) => state.offer?.included?.companies || state.offers?.included?.companies)
  const auth = useAuth()
  const site = useSelector(({ site }) => site)
  const [expanded, setExpanded] = useState(false)

  const gradeClasses = useGradeStyles()

  if (!company || !Object.values(company)[0].display_public_metrics || !auth.loggedIn() || !site) {
    return null
  }

  const averageKpi = Object.values(company)[0].reactivity_average
  const averageClasses = gradeStyle(averageKpi, gradeClasses)

  if (site.entity_type === 'Company') {
    return null
  }

  return (
    <Box className={classes.container}>
      <Divider className={classes.divider} />
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          {i18n.t('company_profiles.kpis.percentage_title')}
        </Typography>
        <IconButton
          size="small"
          className={clsx(expandClasses.expand, expanded && expandClasses.expandOpen)}
          onClick={() => setExpanded(!expanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {!expanded && (
        <Box className={classes.progressContainer}>
          <Box className={classes.progressLabel}>
            <Typography variant="body2" color="textSecondary" className={classes.progressLabelText}>
              {i18n.t('company_profiles.kpis.global_average')}
            </Typography>
            <Typography variant="body2" className={classes.progressLabelValue}>
              {averageKpi}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={averageKpi}
            className={classes.progressBar}
            classes={{
              barColorPrimary: averageClasses.barColorPrimary,
              colorPrimary: averageClasses.colorPrimary
            }}
          />
        </Box>
      )}

      {expanded && <PercentageKpisLayout />}
    </Box>
  )
}

export const PercentageKpisLayout = () => {
  const classes = usePercentageStyles()
  const company = useSelector((state) => state.offer?.included?.companies || state.offers?.included?.companies)
  const auth = useAuth()
  const gradeClasses = useGradeStyles()

  if (!company || !auth.loggedIn()) return null

  const { kpis } = Object.values(company)[0]

  return (
    <Box mt={1.5}>
      {Object.entries(kpis || {})
        .filter(([key]) => key.includes('percentage'))
        .map(([key, value]) => {
          const itemClasses = gradeStyle(value, gradeClasses)

          return (
            <Box key={key} className={classes.kpiItem}>
              <Typography className={classes.kpiTitle}>
                {i18n.t(`company_profiles.kpis.${key}`)}
              </Typography>
              <Box className={classes.progressLabel}>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t('company_profiles.kpis.progression')}
                </Typography>
                <Typography variant="body2" className={classes.progressLabelValue}>
                  {value}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={value}
                className={classes.progressBar}
                classes={{
                  barColorPrimary: itemClasses.barColorPrimary,
                  colorPrimary: itemClasses.colorPrimary
                }}
              />
            </Box>
          )
        })}
    </Box>
  )
}
